import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Loader2, Sparkles, UserCheck, ArrowUpRight, Bot } from "lucide-react";

const ACK_KEY = "qa_ai_ack";

const SUGGESTED = [
  "What does pre-approval actually mean?",
  "What are closing costs?",
  "What happens after my offer is accepted?",
  "What is an escrow account?",
];

export default function Assistant() {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [ack, setAck] = useState(() => localStorage.getItem(ACK_KEY) === "true");
  const scrollRef = useRef(null);

  const acceptAck = (v) => {
    setAck(v);
    localStorage.setItem(ACK_KEY, v ? "true" : "false");
  };

  const { data: questions = [] } = useQuery({
    queryKey: ["questions"], queryFn: async () => (await api.get("/buyer/questions")).data,
  });

  const ask = useMutation({
    mutationFn: async (q) => (await api.post("/qa/ask", { question: q })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["questions"] }); setInput(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const escalate = useMutation({
    mutationFn: async (q) => (await api.post("/qa/escalate", { question: q.question, question_id: q.id })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["questions"] }); toast.success("Sent to your agent"); },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [questions.length]);

  const submit = (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q) return;
    ask.mutate(q);
  };

  return (
    <div>
      <PageHeader title="Ask a question" subtitle="Plain-language answers about the home-buying process. For anything legal, financial, or specific to a home, escalate to your agent." />

      <Card className="p-5 rounded-2xl mb-5 flex items-center gap-4" data-testid="qa-agent-intro">
        <img src="/agent-photo.webp" alt="Marlene Alesna" data-testid="qa-agent-photo"
          className="h-16 w-16 rounded-full object-cover ring-2 ring-primary/30 shrink-0" />
        <div>
          <p className="font-semibold leading-tight">Marlene Alesna</p>
          <p className="text-xs text-muted-foreground">Your agent · eXp Realty · The Agency Team Hawaii</p>
          <p className="text-sm text-muted-foreground mt-1">Ask me anything about the buying process. Anything legal, financial, or home-specific I'll handle with you personally.</p>
        </div>
      </Card>

      {!ack && (
        <Card className="p-5 rounded-2xl mb-5 border-primary/30 bg-primary/5" data-testid="qa-ack-card">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <Bot className="h-4.5 w-4.5 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Before you start — a quick heads up</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                This is an <strong>automated AI assistant</strong> — not Marlene and not a licensed professional. It answers general questions about the home-buying <em>process</em> only. It does <strong>not</strong> give legal, financial, tax, or home-specific advice, and it does not interpret neighborhoods, schools, or safety. For anything about your situation, escalate to your agent or a licensed professional.
              </p>
            </div>
          </div>
          <label className="flex items-start gap-3 mt-4 p-3 rounded-xl bg-background cursor-pointer" data-testid="qa-ack-label">
            <Checkbox checked={ack} onCheckedChange={acceptAck} data-testid="qa-ack-checkbox" className="mt-0.5" />
            <span className="text-sm">I understand this is an AI assistant that provides general information only, not professional advice.</span>
          </label>
        </Card>
      )}

      <Card className={`p-5 rounded-2xl mb-5 ${!ack ? "opacity-50 pointer-events-none select-none" : ""}`} aria-disabled={!ack} data-testid="qa-ask-card">
        <form onSubmit={submit} className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={!ack || ask.isPending} placeholder={ack ? "Ask about the buying process…" : "Check the box above to start"} data-testid="qa-input" className="h-12 rounded-xl" />
          <Button type="submit" disabled={!ack || ask.isPending || !input.trim()} data-testid="qa-send-btn" className="h-12 rounded-xl px-5">
            {ask.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTED.map((s) => (
            <button key={s} onClick={() => ask.mutate(s)} disabled={!ack || ask.isPending} data-testid="qa-suggested"
              className="text-xs px-3 py-1.5 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors disabled:cursor-not-allowed">{s}</button>
          ))}
        </div>
      </Card>

      <div className="space-y-4" ref={scrollRef} data-testid="qa-thread">
        {ask.isPending && (
          <Card className="p-5 rounded-2xl"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div></Card>
        )}
        {questions.length === 0 && !ask.isPending && (
          <Card className="p-10 rounded-2xl text-center">
            <Sparkles className="h-8 w-8 mx-auto text-primary mb-2" />
            <p className="text-muted-foreground text-sm">Ask your first question to get started.</p>
          </Card>
        )}
        {questions.map((q, i) => (
          <motion.div key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-5 rounded-2xl" data-testid={`qa-item-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold">{q.question}</p>
                {q.status === "escalated" && <Badge className="bg-status-warning/15 text-status-warning border-0 shrink-0">Sent to agent</Badge>}
                {q.status === "resolved" && <Badge className="bg-status-complete/15 text-status-complete border-0 shrink-0">Agent replied</Badge>}
              </div>
              {q.answer && (
                <div className="mt-3 pl-3 border-l-2 border-primary/30">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{q.answer}</p>
                </div>
              )}
              {q.agent_reply && (
                <div className="mt-3 p-3 rounded-xl bg-forest/5 border border-forest/20">
                  <p className="text-xs font-semibold text-forest flex items-center gap-1 mb-1"><UserCheck className="h-3.5 w-3.5" /> Your agent</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{q.agent_reply}</p>
                </div>
              )}
              {!q.escalated && q.status !== "resolved" && (
                <Button variant="ghost" size="sm" onClick={() => escalate.mutate(q)} data-testid="qa-escalate-agent-btn" className="mt-3 text-primary gap-1 h-8">
                  <ArrowUpRight className="h-3.5 w-3.5" /> Escalate to my agent
                </Button>
              )}
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="mt-6">
        <Disclaimer>Answers explain general concepts only. Look Before You Leap 808 does not give legal, financial, or tax advice, and does not interpret neighborhoods, schools, or safety. For your situation, escalate to your agent or a licensed professional.</Disclaimer>
      </div>
    </div>
  );
}
